import { type ReactNode } from 'react'
import {
	getProgramFacts,
	getVenuePinData,
	getVenueSpanMeters,
	type ProgramEvent,
} from '#app/domains/program/pure'
import { CountUp } from '../../atoms/CountUp.tsx'

/**
 * The welcome: the organisers' own intro text, the festival in numbers
 * (counted from the program and measured on the map — never from seat
 * capacities, which are still placeholders while the program is planned).
 */
export function FestivalWelcome({
	introText,
	events,
}: {
	introText: string | null
	events: ProgramEvent[]
}) {
	const paragraphs = (introText ?? '')
		.split(/\n\s*\n/)
		.map((paragraph) => paragraph.trim())
		.filter(Boolean)
	const facts = getProgramFacts(events)
	// measured on the map (OSM/Kartverket), not taken from the program data
	const span = getVenueSpanMeters(getVenuePinData(events))

	const numbers: Array<{ value: number; label: ReactNode }> = [
		{ value: facts.events, label: 'arrangement' },
		{ value: facts.venues, label: facts.venues === 1 ? 'scene' : 'scenar' },
		{ value: facts.days, label: facts.days === 1 ? 'dag' : 'dagar' },
	]
	if (span) {
		numbers.push({
			value: span,
			label: (
				<>
					meter <span className="hk-fact-of">mellom scenane, på det meste</span>
				</>
			),
		})
	}

	return (
		<section className="hk-section hk-welcome" aria-labelledby="welcome-title">
			<div className="hk-container">
				<div className="hk-welcome-grid">
					<div className="hk-welcome-text">
						<header className="hk-section-head">
							<p className="hk-kicker">Velkomen</p>
							<h2 id="welcome-title" className="hk-h2">
								Døra står open
							</h2>
						</header>
						{paragraphs.map((paragraph, i) => (
							<p key={i} className={i === 0 ? 'hk-intro-lede' : 'hk-intro'}>
								{paragraph}
							</p>
						))}
					</div>

					{events.length ? (
						<aside className="hk-welcome-side" aria-label="Festivalen i tal">
							<dl className="hk-facts">
								{numbers.map((fact, i) => (
									<div key={i} className="hk-fact">
										<dt className="hk-fact-label">{fact.label}</dt>
										<dd className="hk-fact-value">
											<CountUp value={fact.value} duration={1200 + i * 250} />
										</dd>
									</div>
								))}
							</dl>
						</aside>
					) : null}
				</div>
			</div>
		</section>
	)
}
